import { telegram } from '../../utils/telegram';
import type { DurationOption } from '../../types/business';

interface Props {
  value: number;
  onChange: (months: number) => void;
  options: DurationOption[];
}

/**
 * Custom sliding segmented control for subscription duration.
 * Sliding indicator + per-option `highlightLabel` badge (BE belgilaydi).
 */
export default function DurationSwitcher({ value, onChange, options }: Props) {
  const activeIndex = Math.max(
    0,
    options.findIndex((opt) => opt.months === value),
  );

  const handleChange = (next: number) => {
    if (next === value) return;
    telegram.hapticFeedback.selection();
    onChange(next);
  };

  if (options.length === 0) return null;

  return (
    <div
      role="tablist"
      style={{
        position: 'relative',
        display: 'flex',
        background: 'var(--bl-surface-soft, #eef0f3)',
        borderRadius: 14,
        padding: 4,
        height: 44,
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 4,
          bottom: 4,
          left: 4,
          width: `calc((100% - 8px) / ${options.length})`,
          transform: `translateX(${activeIndex * 100}%)`,
          background: 'var(--bl-surface, #fff)',
          borderRadius: 10,
          boxShadow: '0 2px 6px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.04)',
          transition: 'transform 0.26s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />

      {options.map((opt) => {
        const isActive = value === opt.months;
        return (
          <button
            key={opt.months}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => handleChange(opt.months)}
            style={{
              position: 'relative',
              flex: 1,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: isActive ? 700 : 500,
              color: isActive ? 'var(--bl-text-strong, #0f172a)' : 'var(--bl-text-muted, #6b7280)',
              letterSpacing: isActive ? -0.1 : 0,
              padding: 0,
              fontFamily: 'inherit',
              WebkitTapHighlightColor: 'transparent',
              transition: 'color 0.18s ease, font-weight 0.18s ease',
              outline: 'none',
            }}
          >
            {opt.label}

            {opt.highlightLabel && (
              <span
                style={{
                  position: 'absolute',
                  top: -8,
                  right: 4,
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  padding: '2px 6px',
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #1f8b4c 0%, #27ae60 100%)',
                  color: '#fff',
                  boxShadow: '0 2px 6px rgba(31,139,76,0.40)',
                  border: '2px solid var(--bl-surface-soft, #eef0f3)',
                  lineHeight: 1,
                }}
              >
                {opt.highlightLabel}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
